import { useCallback, useRef } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { FormBase, type IFormModalProps } from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import { ColorPickerSelectData } from '../../../../components/ColorPickerSelectData';
import { globalStyleDefault } from '../../../../constants/GlobalStyles';
import {
  type TemplateColorsSchema,
  templateColorsSchema,
} from '../../../../formSchemas/globalStyleSchema';
import { useCourse } from '../../../../hooks/useCourse';
import {
  generateOldNewAdditionalColors,
  generateOldNewTemplateColors,
  generateTemplateColorsToUpdate,
} from '../shared/globalStyleHelpers';
import { ColorPickerContainer, Container, FormModalCustom } from './styles';

interface IModalSwapColorsProps
  extends Partial<
    Omit<IFormModalProps, 'children' | 'onAccept' | 'onOpenChange'>
  > {
  courseId: number;
  loading: boolean;
  onAccept: (data: TemplateColorsSchema) => Promise<void>;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
  needSave?: boolean;
}

export const ModalSwapColors = ({
  loading,
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  needSave = false,
  ...props
}: IModalSwapColorsProps): JSX.Element | null => {
  const isSelectOpen = useRef(new Set());
  const { selectedCourse } = useCourse();

  const templateColorsForm = useForm<TemplateColorsSchema>({
    resolver: zodResolver(templateColorsSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      templateColors: generateOldNewTemplateColors(
        selectedCourse?.globalStyle?.templateColors ??
          globalStyleDefault.templateColors
      ),
      additionalColors: generateOldNewAdditionalColors(
        selectedCourse?.globalStyle?.additionalColors?.at(0) ??
          globalStyleDefault.additionalColors?.at(0) ??
          {}
      ),
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    trigger,
  } = templateColorsForm;

  const values = watch();

  const resetForm = useCallback(() => {
    const globalStyle = selectedCourse?.globalStyle ?? globalStyleDefault;

    reset({
      templateColors: generateOldNewTemplateColors(
        globalStyle?.templateColors ?? globalStyleDefault.templateColors
      ),
      additionalColors: generateOldNewAdditionalColors(
        globalStyle?.additionalColors?.at(0) ??
          globalStyleDefault.additionalColors?.at(0) ??
          {}
      ),
    });
  }, [reset, selectedCourse?.globalStyle]);

  const onSubmit: SubmitHandler<TemplateColorsSchema> = (
    data: TemplateColorsSchema
  ) => {
    void onAccept?.({
      templateColors: generateTemplateColorsToUpdate(data.templateColors),
      additionalColors: data.additionalColors,
    });
  };

  const handleDeclineSaveGlobalStyle = useCallback(() => {
    if (!isSelectOpen.current.size) {
      onDecline?.();
      resetForm();
    }
  }, [onDecline, resetForm]);

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  const handleTemplatePrimaryColorChange = useCallback(
    (color: string) => {
      const currentColor = values.templateColors.primaryColor;
      const currentAdditionalColors = values.additionalColors ?? [];
      const newAdditionalColors = [...currentAdditionalColors];

      const colorIndex = currentAdditionalColors.findIndex(
        (item) => item?.color === color
      );

      if (colorIndex !== -1) {
        newAdditionalColors[colorIndex] = { color: currentColor };
        setValue('additionalColors', newAdditionalColors);
      }

      setValue('templateColors.primaryColor', color);
      void trigger('templateColors.primaryColor');
    },
    [
      values.templateColors.primaryColor,
      values.additionalColors,
      setValue,
      trigger,
    ]
  );

  const handleTemplateSecondaryColorChange = useCallback(
    (color: string) => {
      const currentColor = values.templateColors.secondaryColor;
      const currentAdditionalColors = values.additionalColors ?? [];
      const newAdditionalColors = [...currentAdditionalColors];

      const colorIndex = currentAdditionalColors.findIndex(
        (item) => item?.color === color
      );

      if (colorIndex !== -1) {
        newAdditionalColors[colorIndex] = { color: currentColor };
        setValue('additionalColors', newAdditionalColors);
      }

      setValue('templateColors.secondaryColor', color);
      void trigger('templateColors.secondaryColor');
    },
    [
      values.templateColors.secondaryColor,
      values.additionalColors,
      setValue,
      trigger,
    ]
  );

  const handleTemplateBackgroundColorChange = useCallback(
    (color: string) => {
      const currentColor = values.templateColors.backgroundColor;
      const currentAdditionalColors = values.additionalColors ?? [];
      const newAdditionalColors = [...currentAdditionalColors];

      const colorIndex = currentAdditionalColors.findIndex(
        (item) => item?.color === color
      );

      if (colorIndex !== -1) {
        newAdditionalColors[colorIndex] = { color: currentColor };
        setValue('additionalColors', newAdditionalColors);
      }

      setValue('templateColors.backgroundColor', color);
      void trigger('templateColors.backgroundColor');
    },
    [
      values.templateColors.backgroundColor,
      values.additionalColors,
      setValue,
      trigger,
    ]
  );

  const handleTemplateAccentColorChange = useCallback(
    (color: string) => {
      const currentColor = values.templateColors.accentColor;
      const currentAdditionalColors = values.additionalColors ?? [];
      const newAdditionalColors = [...currentAdditionalColors];

      const colorIndex = currentAdditionalColors.findIndex(
        (item) => item?.color === color
      );

      if (colorIndex !== -1) {
        newAdditionalColors[colorIndex] = { color: currentColor };
        setValue('additionalColors', newAdditionalColors);
      }

      setValue('templateColors.accentColor', color);
      void trigger('templateColors.accentColor');
    },
    [
      values.templateColors.accentColor,
      values.additionalColors,
      setValue,
      trigger,
    ]
  );

  return !open ? null : (
    <FormBase.Provider {...templateColorsForm}>
      <FormModalCustom
        acceptText="Save"
        dataCy="template-colors-dialog-modal"
        declineText="Cancel"
        mainText="Template Colors"
        disabled={!isValid}
        zIndex={12}
        {...props}
        onAccept={handleSubmit(onSubmit)}
        loading={loading}
        onDecline={handleDeclineSaveGlobalStyle}
        onOpenChange={handleDeclineSaveGlobalStyle}
        open={!waitToOpen}
        acceptButtonTooltipText={
          needSave
            ? 'Please save your course before updating the template colors.'
            : null
        }
      >
        <Container>
          {values.templateColors.primaryColor !== undefined && (
            <ColorPickerContainer>
              <ColorPickerSelectData
                color={values.templateColors.primaryColor}
                dataCy={`template-color-${values.templateColors.primaryColor}`}
                errorMessage={errors.templateColors?.primaryColor?.message}
                label="Primary Color"
                name="templateColors.primaryColor"
                onChange={handleTemplatePrimaryColorChange}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'primaryColor');
                }}
                zIndex={12}
              />
            </ColorPickerContainer>
          )}
          {values.templateColors.secondaryColor !== undefined && (
            <ColorPickerContainer>
              <ColorPickerSelectData
                color={values.templateColors.secondaryColor}
                dataCy={`template-color-${values.templateColors.secondaryColor}`}
                errorMessage={errors.templateColors?.secondaryColor?.message}
                label="Secondary Color"
                name="templateColors.secondaryColor"
                onChange={handleTemplateSecondaryColorChange}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'secondaryColor');
                }}
                zIndex={12}
              />
            </ColorPickerContainer>
          )}
          {values.templateColors.backgroundColor !== undefined && (
            <ColorPickerContainer>
              <ColorPickerSelectData
                color={values.templateColors.backgroundColor}
                dataCy={`template-color-${values.templateColors.backgroundColor}`}
                errorMessage={errors.templateColors?.backgroundColor?.message}
                label="Background Color"
                name="templateColors.backgroundColor"
                onChange={handleTemplateBackgroundColorChange}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'backgroundColor');
                }}
                zIndex={12}
              />
            </ColorPickerContainer>
          )}
          {values.templateColors.accentColor !== undefined && (
            <ColorPickerContainer>
              <ColorPickerSelectData
                color={values.templateColors.accentColor}
                dataCy={`template-color-${values.templateColors.accentColor}`}
                errorMessage={errors.templateColors?.accentColor?.message}
                label="Accent Color"
                name="templateColors.accentColor"
                onChange={handleTemplateAccentColorChange}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'accentColor');
                }}
                zIndex={12}
              />
            </ColorPickerContainer>
          )}
        </Container>
      </FormModalCustom>
    </FormBase.Provider>
  );
};
